import { gql } from '@apollo/client';

export const queryLists = gql`
  query getLists(
    $sort: Sort
    $search: String
    $pagination: Pagination
    $filterInfo: ListFilterInfoInput
    $isEmbedded: Boolean
  ) {
    lists(
      sort: $sort
      search: $search
      pagination: $pagination
      filterInfo: $filterInfo
      isEmbedded: $isEmbedded
    ) {
      info {
        count
        offset
        limit
        page
      }
      results {
        id
        name
        listType
        updatedAt
        createdAt
        creatorUserId
        description
        hasUnsupportedEmbeddedEventType
        hasUnsupportedEmbeddedListReference
      }
    }
  }
`;

export const queryListsCreators = gql`
  query getEmailListsCreators {
    emailListsCreators {
      id
      fullName
    }
  }
`;

export const queryCampaignProjectLabels = gql`
  query getCampaignLabels {
    campaignLabels {
      id
      name
    }
  }
`;

export const daysOfWeek = gql`
  fragment DaysFragment on SkipDaysOfWeek {
    monday
    tuesday
    wednesday
    thursday
    friday
    saturday
    sunday
  }
`;

/**
 * TODO: This is fetching all nodes for the Journey associated with this campaign,
 * which is not ideal, but we don't currently have the ability to filter by campaign id.
 * I have tested this with a Journey with 200 send nodes and did not noticed any vividly noticeable slowdown;
 * This may not be representative of real usage though as nodes complexities may contribute to slowdowns.
 * We'll need to do BE works to only fetch the node associated with the campaign in the future.
 */
export const queryCampaignById = gql`
  ${daysOfWeek}
  query getCampaign($id: Long!) {
    campaign: campaign(id: $id) {
      id
      isJourneyAssociated
      name
      templateId
      createdAt
      creatorUser {
        id
        fullName
        avatarUrl
      }
      startAt
      endedAt
      messageMedium
      mobileAppIds
      placementId
      embeddedPriorityOrder
      priorityLevel
      campaignState
      campaignTypeDefinition
      dataScienceOptimizations
      aiCreation
      campaignAlertsEnabled
      nextRunTime
      journey: workflow {
        id
        name
        workflowType
        allNodes {
          id
          resource {
            ... on CampaignNodeResource {
              campaign {
                id
              }
            }
          }
        }
        goals {
          isActive
          conversions {
            eventName
            attributionMode
            attributionPeriodHours
          }
        }
      }
      experiments {
        id
        state
        name
        createdAt
        experimentType
        winningCriteria {
          type
        }
        variations {
          id
          sendTime
        }
      }
      labels {
        id
        name
      }
      lists {
        id
        name
      }
      suppressionLists {
        id
        name
      }
      conversions {
        events {
          name
          aggregationType
          attributionMode {
            name: __typename
            ... on LastMessaged {
              periodHours
            }
          }
        }
      }
      customQuietHours {
        startTime
        endTime
        sendSkip
      }
      customRateLimit {
        rateLimitPerMin
        useRateLimit
      }
      customConversion {
        eventName
        attributionMode
        attributionPeriodHours
      }
      ignoreFrequencyCap
      schedulerMsgParams {
        scheduleType
        defaultTimeZone
        schedulerMsgIds
        recurDays
        precreateHours
        endDate
        windowHours
        automaticSendTime
        startTimeZone
        skipDaysOfWeek {
          ...DaysFragment
        }
        recurSchedule {
          period
          adjustments {
            ... on DayOfMonth {
              day
              type: __typename
            }
          }
        }
      }
      displayUntil {
        endDate
        searchQuery {
          ... on SearchCombo {
            combinator
            searchQueries {
              ... on SingleValueSearchField {
                dataType
                field
                comparatorType
                value
                dynamicValue
                fieldType
                castType
              }
            }
          }
        }
      }
    }
  }
`;

export const queryTemplateAndContentById = gql`
  query getTemplateAndContent($id: Long!, $locale: TemplateLocaleInput) {
    templateAndSingleContent(id: $id, locale: $locale) {
      ... on TemplateAndContent {
        name
        createdAt
        updatedAt
        validationFailures {
          errors {
            contentId
          }
        }
        messageType {
          id
          name
          messageChannel {
            channelType
            id
            projectId
            messageMedium
          }
        }
        dataFeedIds
        shortenLinks
        templateType
        primaryContent {
          ... on PushContentDefinition {
            interruptionLevel
            relevanceScore
            badge
            contentAvailable
            title
            message
            attachmentUrlIos
            attachmentUrlAndroid
            defaultAction {
              type
              data
            }
            actionButtons {
              title
            }
            sound
            isSilentPush
            payload
          }
          ... on WebPushContentDefinition {
            title
            message
            icon
            clickAction
          }
          ... on InAppContentDefinition {
            inAppDisplaySettings {
              bgColor {
                hex
                alpha
              }
              shouldAnimate
            }
            webInAppDisplaySettings
            payload
            messageFormat
            ottPayload {
              backgroundColor
              dialogText
              dialogHeader
              fhdImage
              dialogBodyFont
              dialogButtonFont
              dialogHeaderFont
              buttons {
                buttonTextColorFocused
                buttonColorFocused
                buttonTextColorUnFocused
                buttonDeeplink
                buttonColorUnFocused
                buttonText
                buttonBorderColor
              }
            }
            beeBuilderMetadata
            html
            inboxMetadata {
              title
              subtitle
              icon
            }
            destinations
            expirationDateTime
            expirationDuration
            trigger
            saveToInbox
          }
          ... on EmailContentDefinition {
            fromEmail
            html
            beeBuilderMetadata
            fromName
            replyTo
            ccEmails
            subject
            preheaderText
            googleAnalyticsCampaignName
            inlineCss
            linkParams {
              key
              value
            }
          }
          ... on SMSContentDefinition {
            message
            googleAnalyticsCampaignName
            imageUrl
            trackingDomain
            linkParams {
              key
              value
            }
            smsSender {
              name
            }
            verifiedContactCardId
          }
          ... on EmbeddedContentDefinition {
            embeddedContentId
            locale {
              name
            }
            title
            body
            elements {
              mediaUrl
              defaultAction {
                type
                data
              }
              buttons {
                id
                title
                action {
                  type
                  data
                }
              }
              text {
                id
                label
                text
              }
            }
            listId
            placementId
            payload
          }
          ... on WhatsAppContentDefinition {
            header {
              ... on TextHeader {
                text
              }
              ... on ImageHeader {
                mediaUrl
              }
            }
            message
            buttons {
              ... on UrlButton {
                buttonText
                title
                url
              }
              ... on CallButton {
                buttonText
                title
                phoneNumber
              }
              ... on QuickReplyButton {
                buttonText
                title
              }
            }
            footer
            whatsAppCategory: category
            whatsAppSender {
              displayName
              sendLimit
            }
          }
        }
      }
    }
  }
`;

export const queryVerifiedContactCardById = gql`
  query getVerifiedContactCardById($id: Long!) {
    getVerifiedContactCardById(id: $id) {
      displayName
    }
  }
`;

export {
  /**
   * @deprecated This export is deprecated, please import `modules/campaigns/lib/hooks/useTemplateLocales` directly.
   */
  queryTemplateLocales,
} from 'modules/campaigns/lib/hooks/useTemplateLocales';
